import React from 'react';

import { Spinner } from './Spinner';

import type { ButtonProps, ButtonVariant, ButtonSize } from './Button.types';

/**
 * Shared button style classes used by Button and LinkButton
 */

/**
 * Variant style classes
 */
export const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-gradient-to-br from-primary-300 to-primary-400 text-primary-950 hover:from-primary-400 hover:to-primary-500 active:bg-pressed-darken transition-all',
  contrast:
    'bg-contrast-500 text-neutral-50 hover:bg-contrast-600 active:bg-pressed-darken',
  'outline-primary':
    'bg-transparent border-2 border-primary-400 text-primary-300 hover:bg-sakura-wash hover:border-stroke-accent-strong active:bg-pressed-darken',
  'outline-contrast':
    'bg-transparent border-2 border-stroke-strong text-contrast-300 hover:bg-contrast-950 active:bg-pressed-darken',
  ghost:
    'bg-transparent text-neutral-50 hover:bg-lift-3 active:bg-pressed-darken',
  'ghost-muted':
    'bg-transparent text-neutral-400 hover:bg-lift-3 hover:text-neutral-50 active:bg-pressed-darken',
  danger:
    'bg-error-500 text-neutral-50 hover:bg-error-400 active:bg-pressed-darken',
  dashed:
    'border-2 border-dashed border-primary-400 bg-sakura-wash text-primary-300 hover:bg-sakura-tint hover:border-solid active:bg-pressed-darken',
};

/**
 * Size style classes
 */
export const SIZE_CLASSES: Record<ButtonSize, string> = {
  xs: 'h-8 px-2 text-label-xs gap-1',
  sm: 'h-9 px-3 text-label-sm gap-1.5',
  md: 'h-10 px-4 text-label-sm gap-2',
  lg: 'h-12 px-6 text-body-md gap-2',
};

/**
 * Icon size classes for left/right icons
 */
export const ICON_SIZE_CLASSES: Record<ButtonSize, string> = {
  xs: 'w-3.5 h-3.5',
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-5 h-5',
};

/**
 * Spinner size classes
 */
export const SPINNER_SIZE_CLASSES: Record<ButtonSize, string> = {
  xs: 'w-3 h-3',
  sm: 'w-4 h-4',
  md: 'w-4 h-4',
  lg: 'w-5 h-5',
};

/**
 * Base classes shared by Button and LinkButton
 *
 * Touch-target floor: `SIZE_CLASSES` sets the visual height, which on a fine
 * pointer (mouse) renders as sized — compact `xs`/`sm`/`md` heights are
 * legitimate in dense desktop UI. On a coarse pointer (touch) the interactive
 * box is floored to 44px (`pointer-coarse:min-h-11`), the documented minimum
 * touch target (design-tokens.json; WCAG 2.5.5), so no size can ship a sub-44px
 * touch target. Width is content-driven, so only the height needs flooring.
 * Mirrors the same guarantee in IconButton/DiscardButton.
 */
export const BASE_CLASSES =
  'inline-flex items-center justify-center pointer-coarse:min-h-11 font-medium transition-colors duration-normal cursor-pointer focus:outline-none focus-visible:ring-state-focus';

/**
 * The default corner, kept OUT of {@link BASE_CLASSES} so `pill` can swap it
 * rather than compete with it. Two radius utilities on one element resolve by
 * stylesheet order, which is silent and not something a caller can reason about.
 */
export const RADIUS_CLASS = 'rounded-lg';

/**
 * Button component - versatile button with multiple variants and sizes
 *
 * Features:
 * - 8 visual variants (primary, secondary, contrast, outline, ghost, danger, success)
 * - 4 sizes (xs, sm, md, lg)
 * - Loading state with spinner
 * - Left/right icon support
 * - Full width option
 * - Accessible focus states
 *
 * @example
 * ```tsx
 * // Primary button
 * <Button variant="primary" onClick={handleClick}>
 *   Save Changes
 * </Button>
 *
 * // Button with icon
 * <Button variant="contrast" leftIcon={<PlusIcon />}>
 *   Add Item
 * </Button>
 *
 * // Loading button
 * <Button variant="primary" loading>
 *   Saving...
 * </Button>
 * ```
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      children,
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      loading = false,
      pill = false,
      leftIcon,
      rightIcon,
      disabled,
      className = '',
      ...props
    },
    ref
  ) {
    const isDisabled = disabled || loading;

    const variantClasses = VARIANT_CLASSES[variant];
    const sizeClasses = SIZE_CLASSES[size];
    const iconSizeClasses = ICON_SIZE_CLASSES[size];
    const spinnerSizeClasses = SPINNER_SIZE_CLASSES[size];

    return (
      <button
        ref={ref}
        type="button"
        disabled={isDisabled}
        className={`
          button
          ${BASE_CLASSES}
          ${pill ? 'rounded-full' : RADIUS_CLASS}
          disabled:opacity-40 disabled:cursor-not-allowed
          ${variantClasses}
          ${sizeClasses}
          ${fullWidth ? 'w-full' : ''}
          ${className}
        `
          .replace(/\s+/g, ' ')
          .trim()}
        {...props}
      >
        {loading ? (
          <Spinner className={spinnerSizeClasses} />
        ) : leftIcon ? (
          <span
            className={`inline-flex shrink-0 items-center justify-center ${iconSizeClasses}`}
          >
            {leftIcon}
          </span>
        ) : null}
        <span className="flex items-center">{children}</span>
        {!loading && rightIcon && (
          <span
            className={`inline-flex shrink-0 items-center justify-center ${iconSizeClasses}`}
          >
            {rightIcon}
          </span>
        )}
      </button>
    );
  }
);

export default Button;
